import type { FetchClient, RequestOptions } from '../../../lib/http/http.types';
import type {
  FilterState,
  PaginationMeta,
  TableColumn,
} from '../../../components/ui/tableTemplate/TableTemplateTypes';
import { appendFilterParams } from '../../../components/ui/tableTemplate/tableUtils';
import {
  EVENT_INBOX_STATUS,
  type EventInboxDetail,
  type EventInboxListItem,
  type EventInboxStats,
  type EventInboxStatus,
} from '../eventInboxTypes';
import type {
  RawEventInboxItem,
  RawEventInboxListResponse,
  RawEventInboxStats,
} from './eventInboxApi.types';
import EVENT_INBOX_ENDPOINTS from './eventInboxEndpoints';

const KNOWN_STATUSES = new Set<string>(Object.values(EVENT_INBOX_STATUS));

function parseStatus(raw: string | undefined): EventInboxStatus {
  const up = (raw ?? '').toUpperCase();
  return KNOWN_STATUSES.has(up) ? (up as EventInboxStatus) : EVENT_INBOX_STATUS.PENDING;
}

function toListItem(raw: RawEventInboxItem): EventInboxListItem {
  return {
    id: raw.id,
    status: parseStatus(raw.status),
    message_id: raw.message_id ?? '',
    exchange: raw.exchange ?? '',
    attempts: raw.attempts ?? 0,
    last_error: raw.last_error ?? null,
    failed_rule: raw.failed_rule ?? null,
    received_at: raw.received_at ?? '',
    processed_at: raw.processed_at ?? null,
  };
}

function toDetail(raw: RawEventInboxItem): EventInboxDetail {
  return {
    ...toListItem(raw),
    vhost: raw.vhost ?? '',
    payload: raw.payload ?? {},
  };
}

// Sort is fixed to `received_at desc` server-side (the read repo ignores other
// columns — HOMCO-772), so every request sends that default.
const FIXED_SORT = { sortBy: 'received_at', sortOrder: 'desc' } as const;

/**
 * Serializes filter/pagination state into the backend's bracket-notation query
 * via the shared {@link appendFilterParams} (same path as payslips/employees):
 * `status[in]`, `message_id[like]`, `exchange[like]`, `received_at[<op>]`,
 * `processed_at[<op>]` — derived from each `FilterValue`'s kind/op (date ops
 * default to `eq`). No bespoke serializer. `columns` supply
 * `databaseName`/`valueAliases` (none needed — the filter keys are the DB columns).
 */
function buildListQuery(
  filters: FilterState<EventInboxListItem>,
  columns: TableColumn<EventInboxListItem>[],
  page: number,
  limit: number,
): string {
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('limit', String(limit));
  qs.set('sortBy', FIXED_SORT.sortBy);
  qs.set('sortOrder', FIXED_SORT.sortOrder);
  appendFilterParams(qs, filters, columns);
  return qs.toString();
}

function resolveMeta(res: RawEventInboxListResponse, page: number, limit: number): PaginationMeta {
  const total = res.meta?.total ?? res.data.length;
  const resolvedLimit = res.meta?.limit ?? limit;
  const resolvedPage = res.meta?.page ?? page;
  const totalPages =
    res.meta?.totalPages ?? Math.max(1, Math.ceil(total / Math.max(1, resolvedLimit)));
  return {
    total,
    page: resolvedPage,
    limit: resolvedLimit,
    totalPages,
    hasNextPage: resolvedPage < totalPages,
    hasPrevPage: resolvedPage > 1,
  };
}

export interface EventInboxListResult {
  items: EventInboxListItem[];
  meta: PaginationMeta;
}

/**
 * Read-only Event Inbox API. Mirrors the codebase factory pattern
 * (`ApiFactory(client)` fed by `useFetch`) — no mutations are exposed.
 */
export function EventInboxApiFactory(client: FetchClient) {
  return {
    getEvents: async (
      filters: FilterState<EventInboxListItem>,
      columns: TableColumn<EventInboxListItem>[],
      page: number,
      limit: number,
      opts?: RequestOptions,
    ): Promise<EventInboxListResult> => {
      const query = buildListQuery(filters, columns, page, limit);
      const res = await client.get<RawEventInboxItem[] | RawEventInboxListResponse>(
        `${EVENT_INBOX_ENDPOINTS.BASE}${query ? `?${query}` : ''}`,
        opts,
      );
      const normalized: RawEventInboxListResponse = Array.isArray(res) ? { data: res } : res;
      return {
        items: (normalized.data ?? []).map(toListItem),
        meta: resolveMeta(normalized, page, limit),
      };
    },

    getEventById: async (id: string, opts?: RequestOptions): Promise<EventInboxDetail> => {
      const raw = await client.get<RawEventInboxItem>(EVENT_INBOX_ENDPOINTS.BY_ID(id), opts);
      return toDetail(raw);
    },

    getEventsStats: async (opts?: RequestOptions): Promise<EventInboxStats> => {
      const raw = await client.get<RawEventInboxStats>(EVENT_INBOX_ENDPOINTS.STATS, opts);
      return {
        pending: raw.pending ?? 0,
        processing: raw.processing ?? 0,
        done: raw.done ?? 0,
        failed: raw.failed ?? 0,
        total: raw.total ?? 0,
        by_failed_rule: raw.by_failed_rule ?? {},
      };
    },
  };
}
